import { useState } from 'react';

import { dialogService, toastService } from '@thefarmersfront/kitchen';

import { ExcelUploadModal } from '~/components/common';

import { usePostOrdersUpload } from '~/hooks/delivery-agency/api';

import { DELIVERY_AGENCY, FILE_SIZE_LIMIT, TOAST_MESSAGE } from '~/constants';

import DeliveryAgencyOrderInsufficientAddressModal from '../DeliveryAgencyOrderInsufficientAddressModal';
import PackingTypeSelect from './PackingTypeSelect';

import type { TypeDeliveryAgencyOrdersUploadResult, TypePostOrdersUploadParams } from '~/types/delivery-agency.types';

const infoTexts = [
  '엑셀 파일 최대 용량은 10MB입니다. 초과할 시 등록에 실패할 수 있습니다.',
  '최대 10초 정도 소요되니 완료 전까지 브라우저를 종료하지 말아주세요.',
  '1개의 주문을 2개 이상의 엑셀 파일에 나눠서 등록하지 말아주세요.',
];

type OutboundGroupType = TypePostOrdersUploadParams['outboundGroupType'];
type InsufficientAddresses = NonNullable<TypeDeliveryAgencyOrdersUploadResult['insufficientAddresses']>;

const DeliveryAgencyOrderExcelUploadModal = ({
  open,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  onCancel: () => void;
  onConfirm: (data: TypeDeliveryAgencyOrdersUploadResult) => void;
}) => {
  const { requestOrdersUpload } = usePostOrdersUpload();

  const [outboundGroupType, setOutboundGroupType] = useState<OutboundGroupType>();
  const [insufficient, setInsufficient] = useState<{ file: File; addresses: InsufficientAddresses } | null>(null);
  const [uploadModalKey, setUploadModalKey] = useState(0);

  const uploadOrders = (file: File, ignoreInsufficientAddress: boolean) => {
    if (!outboundGroupType) return;

    requestOrdersUpload(
      { file, outboundGroupType, ignoreInsufficientAddress },
      {
        onSuccess: (data) => {
          // 불충분 주소가 있으면 한 건도 등록되지 않고 failCount도 0으로 내려온다
          if (data.insufficientAddresses?.length) {
            setInsufficient({ file, addresses: data.insufficientAddresses });
            return;
          }
          onConfirm(data);
        },
        onError: (error) => {
          if (error?.code === DELIVERY_AGENCY.BACKEND_ERROR_DIALOG_CODE) {
            dialogService.addDialog({
              children: error?.message,
            });
            return;
          }
          toastService.addToast({ type: 'error', message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR });
        },
      }
    );
  };

  const handleConfirm = (file: File) => {
    if (FILE_SIZE_LIMIT < file.size) {
      dialogService.addDialog({
        children: '10MB 이하 파일만 첨부할 수 있습니다.',
      });
      return;
    }

    uploadOrders(file, false);
  };

  const handleReattach = () => {
    setInsufficient(null);
    setUploadModalKey((key) => key + 1);
  };

  const handleProceed = () => {
    if (!insufficient) return;
    setInsufficient(null);
    uploadOrders(insufficient.file, true);
  };

  return (
    <>
      <ExcelUploadModal
        key={uploadModalKey}
        title="주문 엑셀 등록"
        infoTexts={infoTexts}
        open={open}
        onCancel={onCancel}
        onConfirm={handleConfirm}
        fileLabel="파일 첨부"
        confirmDisabled={!outboundGroupType}
        extraContent={<PackingTypeSelect value={outboundGroupType} onChange={setOutboundGroupType} />}
      />
      {insufficient && (
        <DeliveryAgencyOrderInsufficientAddressModal
          open
          addresses={insufficient.addresses}
          onReattach={handleReattach}
          onProceed={handleProceed}
        />
      )}
    </>
  );
};

export default DeliveryAgencyOrderExcelUploadModal;
